import React__default__default from 'react';
import baseStyles from '../../scss/honeycomb-base.scss.js';
import hcPackage from '../../package.json.js';

const CLIENT_ID_NAMESPACE = '__honeycomb-react_css_component';
const CLIENT_CLASSNAME_NAMESPACE = 'honeycomb-react-css-tag';
const baseStylesId = "base-styles".concat(CLIENT_ID_NAMESPACE);
const CLIENT_ID_CACHE = {};
const IS_BROWSER = typeof window !== 'undefined' && typeof document !== 'undefined' && document.head;

// generates style tag and appends to head with given id and css as textNode
// insert right after heads open, or right after previous sibling found, if any
const appendStyles = (id, css) => {
  if (IS_BROWSER && !document.getElementById(id)) {
    const node = document.createElement('style');
    node.textContent = css;
    node.className = CLIENT_CLASSNAME_NAMESPACE;
    node.setAttribute('type', 'text/css');
    node.id = id;
    const siblings = document.querySelectorAll(".".concat(CLIENT_CLASSNAME_NAMESPACE));
    if (siblings.length) {
      const child = siblings[siblings.length - 1];
      child.parentNode.insertBefore(node, child.nextSibling);
    } else {
      const {
        firstChild
      } = document.head;
      document.head.insertBefore(node, firstChild);
    }
    return true;
  }
  return false;
};

/**
 * Helper to generate inline style tags and cache css.
 * Puts all the HC-React component styles as a concatenated string into "context.styles",
 * Puts individual style nodes into "context.styleNodes"
 * returns null when no context is provided
 */
const cachedInlineCss = (cache, styles, id) => {
  if (!cache[id]) {
    // eslint-disable-next-line no-param-reassign
    cache[id] = true;
    // eslint-disable-next-line no-param-reassign
    if (!cache.styles) cache.styles = '';
    // creating style node tag with the provided styles
    const node = /*#__PURE__*/React__default__default.createElement("style", {
      id: id,
      className: CLIENT_CLASSNAME_NAMESPACE,
      type: "text/css"
      // eslint-disable-next-line react/no-danger
      ,
      dangerouslySetInnerHTML: {
        __html: styles
      }
    });
    // eslint-disable-next-line no-param-reassign
    cache.styleNodes = !cache.styleNodes ? [] : cache.styleNodes;
    if (id !== baseStylesId) {
      cache.styleNodes.push(node);
      // eslint-disable-next-line no-param-reassign
      cache.styles += styles;
    } else {
      // pushing base styles to the beginning of the array
      cache.styleNodes.unshift(node);
      // @todo figure out how to handle base style during SSR to avoid specificity issues
      // eslint-disable-next-line no-param-reassign
      // cache.styles = styles + cache.styles; // pushing base styles to the beginning
    }
  }

  return null;
};

// Context for server side rendering
const InjecssContext = /*#__PURE__*/React__default__default.createContext({});

// decorator factory
const Injecss = function (component) {
  let {
    raw: styles
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  // actual decorator
  const decorator = Comp => props => {
    // using displayName and namespace string as unique ID for caching,
    // this ensures every honeycomb component has unique cache ID
    const componentID = Comp.displayName + CLIENT_ID_NAMESPACE + hcPackage.version.replace(/\./g, '-');
    // if no css or empty object return component
    if (typeof styles === 'undefined') {
      return /*#__PURE__*/React__default__default.createElement(Comp, props);
    }

    // Add base styles, once
    if (!CLIENT_ID_CACHE[baseStylesId] && IS_BROWSER) {
      appendStyles(baseStylesId, baseStyles.raw);
      CLIENT_ID_CACHE[baseStylesId] = true;
    }

    // append cache to head if client and not cached
    if (!CLIENT_ID_CACHE[componentID] && IS_BROWSER) {
      if (appendStyles(componentID, styles)) {
        // set to true if styles were appended successfully
        CLIENT_ID_CACHE[componentID] = true;
      }
    }

    // server rendered component
    return /*#__PURE__*/React__default__default.createElement(React__default__default.Fragment, null, /*#__PURE__*/React__default__default.createElement(Comp, props), /*#__PURE__*/React__default__default.createElement(InjecssContext.Consumer, null, value => {
      cachedInlineCss(value, styles, componentID);
      cachedInlineCss(value, baseStyles.raw, baseStylesId);
    }));
  };
  // create the component
  const comp = decorator(component);
  // assign it a name
  comp.displayName = "Injecss(".concat(component.displayName, ")");
  // ship it
  return comp;
};

export { CLIENT_CLASSNAME_NAMESPACE, CLIENT_ID_NAMESPACE, Injecss, InjecssContext, appendStyles };
